// pages/integrations/Sms.tsx
import React from "react";
import { MessageSquare, Database, Key, Check, Copy } from "lucide-react";
import useAuth from "../../../../hooks/features/auth/useAuth";
import { Entreprise } from "../../../../types/features/Entreprise";
import {
  IntegrationHeader,
  ApiKeyTable,
  ApiKeyModal,
  ApiDeleteConfirmationModal,
  NewKeyNotification,
  CodeExamples,
  ApiTester,
  FeatureList,
} from "../../../../components/features/integrations";

import { useApiKeyModule } from "../../../../hooks/features/apiKeys/useApiKeyModule";
import type { EndpointExample } from "../../../../components/features/integrations/CodeExamples";
import type { TesterEndpoint } from "../../../../components/features/integrations/ApiTester";

const MODULE = "sms";

export const SmsIntegrationPage: React.FC = () => {
  const { user } = useAuth();
  const entreprise = user?.profile as Entreprise;
  const originUrl = window.location.origin;

  const {
    apiKeys,
    moduleKey,
    loading,
    copied,
    isModalOpen,
    keyToDelete,
    isRegenerating,
    newKeySecret,
    formData,
    setFormData,
    setIsModalOpen,
    setKeyToDelete,
    setNewKeySecret,
    copyToClipboard,
    handleCreateKey,
    handleRegenerate,
    handleDeleteKey,
    handleToggleKey,
  } = useApiKeyModule(MODULE);

  const config = {
    companyId: entreprise?.id || "",
    apiKey: moduleKey?.key || "Aucune clé API trouvée",
    baseUrl: `${originUrl}/api/v1/sms`,
  };

  // ✅ Endpoints pour CodeExamples (URL complète affichée dans les exemples)
  const codeEndpoints: EndpointExample[] = [
    {
      key: "sms-send",
      label: "SMS simple",
      method: "POST",
      endpoint: `${config.baseUrl}/send`,
      body: {
        entreprise_id: config.companyId,
        to: "+229XXXXXXXX",
        message: "Bonjour, votre commande est prête.",
      },
    },
    {
      key: "sms-otp",
      label: "OTP",
      method: "POST",
      endpoint: `${config.baseUrl}/otp`,
      body: {
        entreprise_id: config.companyId,
        to: "+229XXXXXXXX",
        message: "123456 est votre code de vérification",
      },
    },
  ];

  // ✅ Endpoints pour ApiTester (chemin relatif, car MAIN_API_URL est ajouté côté composant)
  const testerEndpoints: TesterEndpoint[] = [
    {
      key: "sms-send",
      label: "SMS simple",
      method: "POST",
      endpoint: "/v1/sms/send",
      body: {
        to: "+229XXXXXXXX",
        message: "Bonjour, votre commande est prête.",
      },
    },
    {
      key: "sms-otp",
      label: "OTP",
      method: "POST",
      endpoint: "/v1/sms/otp",
      body: {
        to: "+229XXXXXXXX",
        message: "123456 est votre code de vérification",
      },
    },
  ];

  const features = [
    "Envoi en masse",
    "Programmation",
    "Templates personnalisés",
    "Statistiques de délivrance",
    "OTP / codes de vérification",
  ];

  if (!entreprise) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <IntegrationHeader
        icon={<MessageSquare size={40} className="text-purple-600 flex-shrink-0" />}
        title="SMS"
        description="Envoyez des SMS à vos clients depuis votre dashboard ou via l'API"
        status="Actif"
        statusColor="green"
        gradientFrom="purple-50"
        gradientTo="pink-50"
      />

      {/* Section spécifique SMS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <Database size={20} className="text-purple-600" />
            <h3 className="font-semibold text-slate-800">Intégration SMS</h3>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">Envoyez des SMS transactionnels ou marketing à vos clients.</p>

          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">ID Entreprise</label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-600">
                {config.companyId}
              </code>
              <button
                onClick={() => copyToClipboard(config.companyId, "companyId")}
                className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
              >
                {copied === "companyId" ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Clé API SMS</label>
              <button
                onClick={() => setIsModalOpen(true)}
                className="p-1 text-primary hover:bg-primary/10 rounded transition-colors"
              >
                <Key size={16} />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-600 break-all">
                {config.apiKey}
              </code>
              <button
                onClick={() => copyToClipboard(config.apiKey, "apiKey")}
                className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
              >
                {copied === "apiKey" ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {loading ? "Chargement..." : "Cette clé est utilisée pour les appels API du module SMS"}
            </p>
          </div>
        </div>
      </div>

      {/* ✅ Exemples de code multi-endpoints */}
      <CodeExamples apiKey={config.apiKey} endpoints={codeEndpoints} />

      {/* ✅ Testeur API multi-endpoints */}
      <ApiTester endpoints={testerEndpoints} apiKey={config.apiKey} companyId={config.companyId} moduleName={MODULE} />

      {/* Gestion des clés API */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key size={20} className="text-amber-600" />
              <h3 className="font-semibold text-slate-800">Gestion des clés API</h3>
              <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                {apiKeys.length} clé(s)
              </span>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              <Key size={16} />
              Nouvelle clé
            </button>
          </div>
        </div>

        <div className="p-5">
          <ApiKeyTable
            apiKeys={apiKeys}
            loading={loading}
            moduleLabels={{
              tickets: "Tickets",
              calls: "Appels",
              email: "Email",
              whatsapp: "WhatsApp",
              sms: "SMS",
              webhook: "Webhook",
              all: "Accès complet",
            }}
            onCopy={copyToClipboard}
            onToggle={handleToggleKey}
            onRegenerate={handleRegenerate}
            onDelete={setKeyToDelete}
            onCreateNew={() => setIsModalOpen(true)}
            copiedId={copied}
            isRegenerating={isRegenerating}
            moduleFilter={MODULE}
          />
        </div>
      </div>

      {/* Features */}
      <FeatureList features={features} />

      {/* Modales */}
      <ApiKeyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateKey}
        formData={formData}
        setFormData={setFormData}
        moduleName={MODULE}
        permissions={["create", "read", "update", "delete", "send"]}
        moduleLabels={{
          tickets: "Tickets",
          calls: "Appels",
          email: "Email",
          whatsapp: "WhatsApp",
          sms: "SMS",
          webhook: "Webhook",
          all: "Accès complet",
        }}
      />

      <ApiDeleteConfirmationModal
        keyToDelete={keyToDelete}
        onConfirm={handleDeleteKey}
        onCancel={() => setKeyToDelete(null)}
      />

      <NewKeyNotification newKey={newKeySecret} onClose={() => setNewKeySecret(null)} onCopy={copyToClipboard} />
    </div>
  );
};
